import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import useAuthStore from '@/utils/authStore';
import projectsService, { Project } from '@/services/projects.service';
import { Skeleton } from './ui/skeleton';
import useProjectsStore from '@/stores/projectsStore';

export function ProjectSelector() {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentProject, setSelectedProjectId, user } = useAuthStore();
  const lastModified = useProjectsStore(state => state.lastModified);

  useEffect(() => {
    setProjects([]);
    if (user) {
      fetchProjects();
    }
  }, [user?._id]);

  useEffect(() => {
    // Refetch projects when they are modified (created/updated/deleted)
    if (lastModified > 0) {
      fetchProjects();
    }
  }, [lastModified]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsService.getProjects({ limit: 100 });
      if (response.status === 'success' && response.data) {
        setProjects(response.data);
        const { selectedProjectId } = useAuthStore.getState();
        if (!selectedProjectId && response.data.length > 0) {
          await setSelectedProjectId(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = async (projectId: number) => {
    await setSelectedProjectId(projectId);
    setOpen(false);
  };

  if (loading) {
    return <Skeleton className="h-10 w-[200px]" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          {currentProject ? currentProject.name : "Select project..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search project..." />
          <CommandEmpty>No project found.</CommandEmpty>
          <CommandGroup>
            {projects.map((project) => (
              <CommandItem
                key={project.id}
                value={project.name}
                onSelect={() => handleSelectProject(project.id)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    currentProject?.id === project.id ? "opacity-100" : "opacity-0"
                  )}
                />
                {project.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}